import { IntegrationData } from "../types"

export const gatsbyEventsData: IntegrationData = {
  platform: {
    slug: "gatsby-events",
    name: "Gatsby Events",
    description:
      "Elevate your exclusive events with personalized experiences and premium guest amplification",
    shortDescription: "Premium event management and guest amplification",
    logoUrl:
      "https://framerusercontent.com/images/jkOE20pLGj1fhwkaeuJ0UvOM74.svg",
    websiteUrl: "https://www.gatsby.events/",
    category: "event-management",
    tags: ["Event Management", "Premium Events"],
    isComingSoon: false,
  },
  heroTitle: "Elevate Your Exclusive Events with Personalized Experiences",
  heroDescription:
    "We're excited to announce our native integration with Gatsby Events, the platform for curated, high-end event experiences. This integration transforms your exclusive guest list into brand ambassadors – driving awareness and demand through personalized, beautifully designed shareable content.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You're hosting exclusive, curated events on Gatsby Events – product launches, VIP gatherings, invite-only experiences. Your guest list is carefully crafted. Every attendee matters. But here's the opportunity you're missing: your exclusive guests are influencers in their own circles. When they attend your event, their networks pay attention. If you could turn each guest into an ambassador for your event, the reach would multiply exponentially. The problem? Exclusive events often stay exclusive – even in terms of buzz. Your guests don't have easy, elegant ways to share their attendance without it feeling promotional or off-brand.",
  challengePoints: [
    {
      title: "Limited Organic Reach",
      description:
        "Exclusive events often stay exclusive – even in terms of buzz. Your guests don't have easy, elegant ways to share their attendance without it feeling promotional or off-brand.",
    },
    {
      title: "Manual Poster Creation",
      description:
        "Creating personalized marketing materials for each guest is time-consuming and requires design skills. Without automation, you can't scale poster creation to your entire guest list.",
    },
    {
      title: "Missed Amplification Opportunities",
      description:
        "Your exclusive guests are influencers in their own circles. Without a tool to help them share, you miss the opportunity to multiply your reach exponentially.",
    },
  ],
  capabilities: [
    {
      title: "Auto-Generate Premium Personalized Posters from Guest Data",
      description:
        "Because we sync all guest information from Gatsby Events – including profile photos, companies, and titles – we can automatically create stunning, personalized posters for every guest. Each poster is personalized with guest's name, profile photo, company and position/title, and event branding.",
      features: [
        "Guest's name and profile photo",
        "Company and position/title",
        "Event branding and details",
        "No manual work or design skills needed",
      ],
    },
    {
      title: "Launch Campaigns to Drive Buzz and Demand",
      description:
        "Exclusive events thrive on anticipation and social proof. Send personalized poster campaigns to amplify the buzz with 'I'm attending' announcements, pre-event hype campaigns, and VIP-treatment emails.",
      features: [
        "'I'm attending' announcements for LinkedIn and social media",
        "Pre-event hype campaigns with countdown messaging",
        "VIP-treatment emails that make guests feel special",
        "Track which guests are generating the most engagement",
      ],
    },
    {
      title: "Complete Guest Roster Sync",
      description:
        "The integration automatically imports your full guest list from Gatsby Events, including full name, email address, profile picture, company/organization, position/role, LinkedIn profile, and all custom registration fields.",
      features: [
        "Full name and email address",
        "Profile picture and company/organization",
        "Position/role and LinkedIn profile",
        "All custom registration fields",
      ],
    },
    {
      title: "Profile Photo Import for Personalization",
      description:
        "Gatsby Events captures guest profile photos – and Premagic puts them to work for poster personalization, AI-powered photo recognition, and personal galleries.",
      features: [
        "Poster personalization with guest photos",
        "AI-powered photo recognition for event photos",
        "Personal galleries for every guest",
        "Premium, personalized experience matching event exclusivity",
      ],
    },
    {
      title: "Smart Custom Field Detection",
      description:
        "Gatsby Events lets you capture rich guest information. Premagic intelligently maps these fields for maximum personalization and targeting.",
      features: [
        "Company fields → Organization for poster display",
        "Position fields → Title/role for professional context",
        "LinkedIn fields → Social profile for networking features",
        "All custom fields available for segmentation",
      ],
    },
    {
      title: "Multi-Organization Support",
      description:
        "Managing events across multiple brands or organizations? The integration supports organization-level access, keeping your guest data properly segmented and secure.",
      features: [
        "Multi-brand event management",
        "Organization-level access control",
        "Proper data segmentation",
        "Secure guest data handling",
      ],
    },
    {
      title: "AI-Powered Photo Distribution (Post-Event)",
      description:
        "After the event, the integration powers personalized photo delivery. Guest profile photos enable instant facial recognition, and event photos are automatically tagged and distributed.",
      features: [
        "Guest profile photos enable facial recognition",
        "Event photos automatically tagged and distributed",
        "Every guest gets their own curated photo gallery",
        "Drive post-event engagement and social sharing",
      ],
    },
  ],
  flywheelTitle: "The Exclusivity Amplification Effect",
  flywheelDescription:
    "Here's how it works for premium events: Exclusivity becomes aspirational. Attendance becomes a status symbol.",
  flywheelSteps: [
    {
      number: 1,
      title: "Curate",
      description: "Build your exclusive guest list on Gatsby Events",
    },
    {
      number: 2,
      title: "Sync",
      description: "Premagic imports guest data including profile photos",
    },
    {
      number: 3,
      title: "Personalize",
      description: "Generate stunning posters for each guest",
    },
    {
      number: 4,
      title: "Elevate",
      description: "Send premium 'You're invited' or 'I'm attending' campaigns",
    },
    {
      number: 5,
      title: "Share",
      description: "Guests post their personalized content to their networks",
    },
    {
      number: 6,
      title: "Intrigue",
      description: "Their followers see the exclusive event and want in",
    },
    {
      number: 7,
      title: "Demand",
      description: "Build waitlists and buzz for current and future events",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Connect",
      description: "Link your Gatsby Events credentials to Premagic",
    },
    {
      number: 2,
      title: "Select",
      description:
        "Choose which Gatsby event to connect to your Premagic project",
    },
    {
      number: 3,
      title: "Configure",
      description:
        "Select which guest fields to sync (company, title, LinkedIn, etc.)",
    },
    {
      number: 4,
      title: "Sync",
      description: "Import your curated guest list with a single click",
    },
    {
      number: 5,
      title: "Generate",
      description: "Auto-create personalized posters for every guest",
    },
    {
      number: 6,
      title: "Campaign",
      description:
        "Send elegant campaigns that match your event's premium positioning",
    },
    {
      number: 7,
      title: "Track",
      description: "Monitor engagement and social reach",
    },
    {
      number: 8,
      title: "Deliver",
      description: "Post-event, distribute personalized photo galleries",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Pre-filled Posters",
      description: "Auto-generate personalized posters from synced guest data",
    },
    {
      feature: "Campaign Distribution",
      description: "Send premium poster campaigns to drive sharing",
    },
    {
      feature: "Full Event Sync",
      description:
        "Import all Gatsby events with details, dates, and cover images",
    },
    {
      feature: "Guest Roster Import",
      description: "Sync names, emails, and profile photos",
    },
    {
      feature: "Profile Photo Import",
      description:
        "Use Gatsby profile pictures for poster personalization and facial recognition",
    },
    {
      feature: "Company & Title Mapping",
      description: "Auto-detect and map company and position fields",
    },
    {
      feature: "LinkedIn Detection",
      description: "Identify and import LinkedIn profile fields",
    },
    {
      feature: "Custom Field Support",
      description: "Import all Gatsby registration fields",
    },
    {
      feature: "Pagination Support",
      description: "Handle guest lists of any size",
    },
    {
      feature: "Multi-Organization",
      description: "Support for multiple brands and organizations",
    },
    {
      feature: "Token Auto-Refresh",
      description: "Seamless API authentication management",
    },
    {
      feature: "AI Photo Distribution",
      description: "Post-event photo delivery to premium guests",
    },
  ],
  targetAudience: [
    {
      title: "Luxury Brand Marketers",
      description: "Hosting exclusive launch events and experiences",
    },
    {
      title: "Event Agencies",
      description: "Managing high-end client events on Gatsby",
    },
    {
      title: "Corporate Event Teams",
      description: "Running VIP hospitality and executive gatherings",
    },
    {
      title: "PR and Communications Teams",
      description: "Creating buzz-worthy moments",
    },
    {
      title: "Founders and Executives",
      description:
        "Hosting invite-only events for partners, investors, or customers",
    },
  ],
  ctaTitle:
    "Ready to Turn Your Gatsby Events Guest List Into Brand Ambassadors?",
  ctaDescription:
    "Your exclusive guests aren't just attendees – they're influencers in their own right. They have networks that trust their taste and judgment. When they share, people pay attention. With Premagic's Gatsby Events integration, you can import premium guest data, create elegant personalization, amplify through exclusivity, drive demand, and extend the experience with post-event photo galleries. Your exclusive events deserve exclusive amplification.",
  seo: {
    title: "Premagic + Gatsby Events Integration | Elevate Exclusive Events",
    description:
      "Transform your Gatsby Events guest list into brand ambassadors with Premagic's native integration. Auto-generate personalized posters, sync guest data, and drive event buzz through premium amplification.",
    keywords: [
      "gatsby events",
      "event management",
      "exclusive events",
      "guest amplification",
      "event marketing",
      "premium events",
      "poster creation",
      "event promotion",
      "vip events",
    ],
  },
}
